import React, { Suspense, lazy, useState } from "react";
import { Smile } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

// Lazy-load so the ~600KB emoji dataset doesn't ship with the initial bundle.
const EmojiPicker = lazy(() => import("emoji-picker-react"));

/**
 * EmojiButton — pill button that opens an emoji picker popover. Calls
 * `onPick(emoji)` with the selected unicode emoji string. Theme controls
 * both the button styling and the picker's internal dark/light theme.
 */
export default function EmojiButton({ onPick, theme = "dark", testId }) {
  const [open, setOpen] = useState(false);
  const dark = theme === "dark";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          title="Insert emoji"
          className={`h-11 w-11 rounded-xl border flex-shrink-0 flex items-center justify-center transition-colors ${
            dark
              ? "border-white/10 text-white/70 hover:text-paul-yellow hover:border-paul-yellow/60"
              : "border-gray-200 text-gray-500 hover:text-paul-yellow hover:border-paul-yellow"
          }`}
        >
          <Smile className="w-5 h-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        data-testid={testId ? `${testId}-picker` : undefined}
        className="p-0 border-none w-auto bg-transparent shadow-none"
      >
        <Suspense
          fallback={
            <div
              className={`w-[320px] h-[400px] rounded-lg flex items-center justify-center text-xs ${
                dark ? "bg-[#10162a] text-white/60" : "bg-white text-gray-400"
              }`}
            >
              Loading…
            </div>
          }
        >
          <EmojiPicker
            theme={dark ? "dark" : "light"}
            width={320}
            height={380}
            lazyLoadEmojis
            previewConfig={{ showPreview: false }}
            skinTonesDisabled
            searchPlaceholder="Search emoji…"
            onEmojiClick={(emoji) => {
              try {
                onPick?.(emoji.emoji);
              } finally {
                setOpen(false);
              }
            }}
          />
        </Suspense>
      </PopoverContent>
    </Popover>
  );
}
